import * as turf from '@turf/turf';
import { useState } from 'react';
import type { UrbanNetworkEdge } from './api';

/**
 * Cola de revisión secuencial de paradas `needs_review` — orden estable
 * (snapshot tomado al arrancar, nunca se reordena mientras se revisa,
 * aunque cambien los datos reales por otro proceso). Puramente UI: no
 * marca nada como revisado en la base, eso lo hace `useSessionOverrides`
 * (en memoria) hasta que haya persistencia real.
 */
export interface ReviewWorkflowState {
  active: boolean;
  index: number;
  total: number;
  currentStopId: string | null;
  /** Se incrementa en cada `start()`/`next()`/`prev()` — el mapa lo usa como disparador de auto-zoom, distinto de un simple cambio de `stopPoint` (que también ocurre al clickear manualmente una parada, donde NO queremos forzar zoom). */
  focusToken: number;
  start: () => void;
  next: () => void;
  prev: () => void;
  exit: () => void;
}

export function useReviewWorkflow(needsReviewStopIds: string[]): ReviewWorkflowState {
  const [active, setActive] = useState(false);
  const [queue, setQueue] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [focusToken, setFocusToken] = useState(0);

  return {
    active,
    index,
    total: queue.length,
    currentStopId: active && queue.length > 0 ? (queue[index] ?? null) : null,
    focusToken,
    start: () => {
      setQueue([...needsReviewStopIds]);
      setIndex(0);
      setActive(true);
      setFocusToken(t => t + 1);
    },
    next: () => {
      setIndex(i => Math.min(i + 1, Math.max(queue.length - 1, 0)));
      setFocusToken(t => t + 1);
    },
    prev: () => {
      setIndex(i => Math.max(i - 1, 0));
      setFocusToken(t => t + 1);
    },
    exit: () => setActive(false),
  };
}

/**
 * Estado "confirmado en esta sesión" — SOLO EN MEMORIA (`useState`, nunca
 * `localStorage`/`sessionStorage`/DB/archivo). Desaparece al recargar la
 * página. Deliberadamente NO se mezcla con los campos reales de
 * `SystemMapStopDetail` (edgeId/status/etc): se guarda aparte y la UI lo
 * muestra siempre con una etiqueta explícita ("confirmado en esta sesión —
 * no persistido"), para que nunca se confunda con un dato real de la base.
 * No hay datasource de escritura seguro hoy — ver auditoría Fase A.
 */
export interface StopOverride {
  confirmedInSession: true;
  /** Snapshot del estado real al momento de confirmar — solo para mostrar en el panel, nunca se reescribe sobre `detail`. */
  confirmedRealStatus: string | null;
}

export function useSessionOverrides(): {
  overrides: Map<string, StopOverride>;
  confirmInSession: (stopId: string, realStatus: string | null) => void;
  clearOverride: (stopId: string) => void;
  getOverride: (stopId: string) => StopOverride | undefined;
} {
  const [overrides, setOverrides] = useState<Map<string, StopOverride>>(new Map());
  return {
    overrides,
    confirmInSession: (stopId, realStatus) => {
      setOverrides(prev => {
        const next = new Map(prev);
        next.set(stopId, { confirmedInSession: true, confirmedRealStatus: realStatus });
        return next;
      });
    },
    clearOverride: stopId => {
      setOverrides(prev => {
        const next = new Map(prev);
        next.delete(stopId);
        return next;
      });
    },
    getOverride: stopId => overrides.get(stopId),
  };
}

/** Edge más cercano a un punto entre los ya cargados de "Red urbana" — cálculo puramente client-side, para preview, nunca se persiste ni se manda al backend como si fuera un resultado NPA real. */
export function findNearestEdge(
  point: [number, number],
  edges: UrbanNetworkEdge[]
): { edge: UrbanNetworkEdge; distanceM: number } | null {
  let best: { edge: UrbanNetworkEdge; distanceM: number } | null = null;
  for (const edge of edges) {
    const nearest = turf.nearestPointOnLine(edge.geometry, point);
    const distanceM = turf.distance(point, nearest.geometry.coordinates, { units: 'meters' });
    if (!best || distanceM < best.distanceM) {
      best = { edge, distanceM };
    }
  }
  return best;
}

/**
 * Umbral de conveniencia para la advertencia de "parada cercana ya
 * existente" — NO es un valor oficial del sistema: la auditoría Fase A no
 * encontró ninguna tolerancia de distancia parada-a-parada persistida en
 * ningún lado para reusar. Se marca así, explícitamente, en la UI.
 */
export const DUPLICATE_WARNING_THRESHOLD_M = 25;

/** Parada real más cercana a un punto — para la advertencia de posible duplicado del flujo "Nueva parada". */
export function findNearestStop(
  point: [number, number],
  stops: { id: string; lon: number | null; lat: number | null; name: string | null }[]
): { id: string; name: string | null; distanceM: number } | null {
  let best: { id: string; name: string | null; distanceM: number } | null = null;
  for (const s of stops) {
    if (s.lon === null || s.lat === null) continue;
    const distanceM = turf.distance(point, [s.lon, s.lat], { units: 'meters' });
    if (!best || distanceM < best.distanceM) {
      best = { id: s.id, name: s.name, distanceM };
    }
  }
  return best;
}

export interface NewStopDraft {
  lon: number;
  lat: number;
  name: string;
  municipalStopId: string;
  /** Edge sugerido automáticamente (nearest-edge client-side) al colocar el marker. */
  proposedEdgeId: string | null;
  /** Si el usuario elige otro edge manualmente — tiene prioridad sobre `proposedEdgeId` para el preview. */
  manualEdgeId: string | null;
}

/**
 * Flujo "Nueva parada" — armar → clic en mapa coloca marker provisional →
 * preview de asociación automática → el usuario puede completar
 * nombre/ID y cambiar el edge manualmente. Nunca crea nada real: sin
 * `Guardar parada` habilitado, esto es puramente un marker de UI.
 */
export function useNewStopDraft(): {
  mode: boolean;
  draft: NewStopDraft | null;
  arm: () => void;
  disarm: () => void;
  placeDraft: (lon: number, lat: number, proposedEdgeId: string | null) => void;
  updateDraft: (patch: Partial<NewStopDraft>) => void;
  clearDraft: () => void;
} {
  const [mode, setMode] = useState(false);
  const [draft, setDraft] = useState<NewStopDraft | null>(null);

  return {
    mode,
    draft,
    arm: () => setMode(true),
    disarm: () => setMode(false),
    placeDraft: (lon, lat, proposedEdgeId) => {
      setDraft({ lon, lat, name: '', municipalStopId: '', proposedEdgeId, manualEdgeId: null });
      setMode(false);
    },
    updateDraft: patch => setDraft(prev => (prev ? { ...prev, ...patch } : prev)),
    clearDraft: () => setDraft(null),
  };
}
